import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import styled from 'styled-components';

import {
  AuthContext,
  baseUrl,
  savePendingMcpConsent
} from 'framework';

const Shell = styled(Box)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${p => p.theme.spacing(2)}px;
`;

const Card = styled(Box)`
  max-width: 460px;
  width: 100%;
  border: 1px solid ${p => p.theme.palette.divider};
  border-radius: 16px;
  padding: ${p => p.theme.spacing(4)}px;
  background: ${p => p.theme.palette.background.paper};
  box-shadow: 0 24px 60px rgba(0, 48, 96, 0.12);
  text-align: center;
`;

const Actions = styled(Box)`
  display: flex;
  gap: ${p => p.theme.spacing(2)}px;
  justify-content: center;
  margin-top: ${p => p.theme.spacing(3)}px;
`;

type Status = 'loading' | 'needsSignin' | 'consent' | 'connecting' | 'error';

export const McpConsent = () => {
  const { t } = useTranslation();
  const { organization } = useParams<{ organization: string }>();
  const { init, isInitialized, isSignedIn, requestSignin, getToken } = useContext(
    AuthContext
  );
  const [status, setStatus] = useState<Status>('loading');
  const [clientName, setClientName] = useState('');

  const search = window.location.search;
  const clientId = new URLSearchParams(search).get('client_id') || '';

  useEffect(() => {
    let active = true;
    const bootstrap = async () => {
      if (!isInitialized()) {
        await init(organization);
      }
      if (clientId) {
        try {
          const response = await fetch(
            `${baseUrl}/org/${organization}/mcp/oauth/client?client_id=${encodeURIComponent(
              clientId
            )}`
          );
          if (active && response.ok) {
            const data = (await response.json()) as { client_name?: string };
            setClientName(data.client_name || '');
          }
        } catch {
          // Ignore lookup failure and use generic consent copy.
        }
      }
      if (!active) {
        return;
      }
      setStatus(isSignedIn() ? 'consent' : 'needsSignin');
    };
    bootstrap();
    return () => {
      active = false;
    };
  }, [organization, init, isInitialized, isSignedIn, clientId]);

  const handleSignin = () => {
    // Preserve the pending OAuth request across the login round trip.
    savePendingMcpConsent(organization, search);
    // The user already chose to connect on this screen and the workspace is
    // known, so go straight to login (skip the /signin chooser bounce) — the
    // single-step experience OIDC already gets. After login the consent resumes
    // via the pending consent saved above.
    requestSignin({ skipChooser: true });
  };

  const handleAllow = async () => {
    setStatus('connecting');
    try {
      const params = new URLSearchParams(search);
      params.set('response_mode', 'json');
      const response = await fetch(
        `${baseUrl}/org/${organization}/mcp/oauth/authorize?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${getToken().getJwtToken()}`,
            Accept: 'application/json'
          }
        }
      );
      const data = (await response.json()) as { redirect?: string };
      if (response.ok && data.redirect) {
        window.location.href = data.redirect;
        return;
      }
      setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'loading' || status === 'connecting') {
    return (
      <Shell>
        <Card>
          <CircularProgress />
          <Typography style={{ marginTop: 16 }}>
            {status === 'connecting'
              ? t('mcpConsentConnecting')
              : t('loading')}
          </Typography>
        </Card>
      </Shell>
    );
  }

  if (status === 'error') {
    return (
      <Shell>
        <Card>
          <Typography color="error">{t('mcpConsentError')}</Typography>
          <Actions>
            <Button variant="contained" color="primary" onClick={handleAllow}>
              {t('mcpConsentAllow')}
            </Button>
          </Actions>
        </Card>
      </Shell>
    );
  }

  if (status === 'needsSignin') {
    return (
      <Shell>
        <Card>
          <Typography variant="h6" gutterBottom>
            {t('mcpConsentTitle')}
          </Typography>
          <Actions>
            <Button variant="contained" color="primary" onClick={handleSignin}>
              {t('mcpConsentSignInPrompt')}
            </Button>
          </Actions>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card>
        <Typography variant="h6" gutterBottom>
          {t('mcpConsentTitle')}
        </Typography>
        <Typography color="textSecondary">
          {t('mcpConsentBody', {
            client: clientName || t('mcpConsentTitle'),
            org: organization
          })}
        </Typography>
        <Actions>
          <Button onClick={() => window.history.back()}>
            {t('mcpConsentDeny')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleAllow}>
            {t('mcpConsentAllow')}
          </Button>
        </Actions>
      </Card>
    </Shell>
  );
};
